<template>
  <div class="building-management-page">
    <CCard class="staff-ui-card bg-white mb-4">
      <CCardHeader class="staff-ui-header d-flex flex-wrap justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="header-main-icon mr-3 d-flex align-items-center justify-content-center">
            <CIcon name="cil-building" size="xl" />
          </div>
          <div>
            <h4 class="card-title mb-0 text-white font-weight-bold">
              {{ $t('buildingMgmt.pageTitle') }}
            </h4>
            <div class="header-sub-text mt-1 d-flex align-items-center">
              <CIcon name="cil-location-pin" class="mr-1 opacity-75" size="sm" />
              <span>{{ $t('buildingMgmt.pageSubtitle') }}</span>
            </div>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2 btn-add-mobile flex-wrap mt-2 mt-md-0">
          <CButton
            v-if="selectedBuildings.length > 0"
            color="danger"
            class="staff-ui-header-button staff-ui-header-button--danger mr-2"
            @click="deleteMultipleBuildings()"
          >
            <CIcon name="cil-trash" /> {{ $t('staffMgmt.deleteSelected') }} ({{ selectedBuildings.length }})
          </CButton>
          <CButton class="staff-ui-header-button staff-ui-header-button--add" @click="openAddModal()">
            <CIcon name="cil-plus" />
            &nbsp;{{ $t('buildingMgmt.addBuildingModal') }}
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody class="staff-ui-body p-3 p-md-4 bg-light">
        <!-- Control Bar: Search & Global Actions -->
        <div class="control-bar-surface d-flex flex-wrap justify-content-between align-items-center gap-3 bg-white p-3 border mb-4 shadow-sm">
          <!-- Search Input -->
          <div class="d-flex align-items-center flex-grow-1" style="max-width: 480px;">
            <div class="input-group search-input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-white border-right-0 text-muted">
                  <CIcon name="cil-search" />
                </span>
              </div>
              <input
                v-model.trim="searchKeyword"
                type="text"
                class="form-control border-left-0"
                :placeholder="$t('buildingMgmt.searchPlaceholder')"
              />
              <div class="input-group-append" v-if="searchKeyword">
                <button class="btn btn-outline-secondary border-left-0" type="button" @click="searchKeyword = ''">✕</button>
              </div>
            </div>
          </div>

          <!-- Quick Stats & Expand/Collapse Toggle & Select All -->
          <div class="d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              class="btn-admin-action btn-admin-action--select-all"
              @click="toggleSelectAll(!isAllSelected)"
            >
              <input
                type="checkbox"
                class="mr-2"
                :checked="isAllSelected"
                style="cursor: pointer; pointer-events: none;"
              />
              <span>{{ isAllSelected ? $t('buildingMgmt.deselectAll') : $t('buildingMgmt.selectAll') }}</span>
            </button>

            <button
              type="button"
              class="btn-admin-action btn-admin-action--toggle-all"
              @click="toggleAllBuildings"
            >
              <CIcon :name="allExpanded ? 'cil-chevron-top' : 'cil-chevron-bottom'" class="mr-1" size="sm" />
              <span>{{ allExpanded ? $t('buildingMgmt.collapseAll') : $t('buildingMgmt.expandAll') }}</span>
            </button>

            <span class="badge-stat badge-stat--building">
              {{ filteredBuildings.length }} {{ $t('buildingMgmt.buildingsCount') }}
            </span>
            <span class="badge-stat badge-stat--rooms">
              {{ totalRoomsCount }} {{ $t('buildingMgmt.roomsCount') }}
            </span>
          </div>
        </div>

        <!-- Buildings Container (Accordion Box Cards) -->
        <div class="buildings-container">
          <div
            v-for="b in filteredBuildings"
            :key="b._id"
            class="building-big-box card mb-3 border-0"
            :class="{
              'building-box--has-rooms': (b.rooms || []).length > 0,
              'building-box--empty': (b.rooms || []).length === 0,
              'building-box--expanded': isBuildingExpanded(b._id)
            }"
          >
            <!-- Card Header: Click to toggle dropdown -->
            <div
              class="card-header building-header py-3 px-3 px-md-4 d-flex flex-wrap justify-content-between align-items-center"
              @click="toggleBuilding(b._id)"
              style="cursor: pointer;"
            >
              <div class="d-flex align-items-center">
                <!-- Checkbox for multi-select (click.stop) -->
                <div class="mr-3 d-flex align-items-center" @click.stop>
                  <input
                    type="checkbox"
                    :value="b._id"
                    v-model="selectedBuildings"
                    class="building-select-checkbox"
                  />
                </div>

                <!-- Building Icon Badge -->
                <div class="building-badge-icon mr-3 shadow-sm d-flex align-items-center justify-content-center">
                  <CIcon name="cil-building" size="lg" />
                </div>

                <!-- Building Title, Code, Badges, and Subtitle -->
                <div>
                  <div class="d-flex align-items-center gap-1 flex-wrap">
                    <h5 class="mb-0 font-weight-bold text-white building-title-text">
                      {{ buildingLabel(b) }}
                    </h5>
                    <span v-if="b.buildingCode || b.BUILDINGCODE" class="badge-code badge-code--building ml-2">
                      {{ b.buildingCode || b.BUILDINGCODE }}
                    </span>
                    <span
                      class="badge-count-pill ml-2"
                      :class="(b.rooms || []).length > 0 ? 'badge-count-pill--has-rooms' : 'badge-count-pill--empty'"
                    >
                      {{ (b.rooms || []).length }} {{ $t('buildingMgmt.roomsCount') }}
                    </span>
                  </div>
                  <div v-if="secondaryBuildingLabel(b)" class="building-meta-text mt-1">
                    <span>{{ secondaryBuildingLabel(b) }}</span>
                  </div>
                </div>
              </div>

              <!-- Header Action Buttons (click.stop) -->
              <div class="building-actions-wrapper d-flex align-items-center gap-2 flex-wrap mt-2 mt-lg-0" @click.stop>
                <button
                  type="button"
                  class="btn-building-action btn-building-action--add-room"
                  @click="openAddModalForBuilding(b)"
                  :title="$t('buildingMgmt.addRoomToBuilding')"
                >
                  <CIcon name="cil-plus" size="sm" class="mr-1" />
                  <span>{{ $t('buildingMgmt.quickAddRoom') }}</span>
                </button>
                <button
                  type="button"
                  class="btn-building-action btn-building-action--edit"
                  @click="openEditBuildingModal(b)"
                  :title="$t('buildingMgmt.editTooltip')"
                >
                  <CIcon name="cil-pencil" size="sm" class="mr-1" />
                  <span>{{ $t('mgmt.edit') }}</span>
                </button>
                <button
                  type="button"
                  class="btn-building-action btn-building-action--delete"
                  @click="confirmDelete(b)"
                  :title="$t('buildingMgmt.deleteBuildingTooltip')"
                >
                  <CIcon name="cil-trash" size="sm" class="mr-1" />
                  <span>{{ $t('mgmt.delete') }}</span>
                </button>
                <button
                  type="button"
                  class="btn-building-action btn-building-action--toggle"
                  @click="toggleBuilding(b._id)"
                >
                  <CIcon :name="isBuildingExpanded(b._id) ? 'cil-chevron-top' : 'cil-chevron-bottom'" size="sm" class="mr-1" />
                  <span>{{ isBuildingExpanded(b._id) ? $t('buildingMgmt.collapse') : $t('buildingMgmt.expand') }}</span>
                </button>
              </div>
            </div>

            <!-- Card Body: Shows rooms when dropdown is expanded -->
            <div v-show="isBuildingExpanded(b._id)" class="card-body building-body p-3 p-md-4 bg-white">
              <div v-if="b.rooms && b.rooms.length > 0" class="table-responsive">
                <table class="table table-hover align-middle mb-0 room-nested-table">
                  <thead class="room-table-thead">
                    <tr>
                      <th style="width: 70px;" class="text-center">{{ $t('buildingMgmt.roomNumber') }}</th>
                      <th>{{ $t('buildingMgmt.roomCol') }}</th>
                      <th style="width: 170px;" class="text-center">{{ $t('mgmt.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(room, rIdx) in b.rooms" :key="room._id || room.ROOMID || rIdx">
                      <td class="text-center text-muted font-weight-bold">{{ rIdx + 1 }}</td>
                      <td>
                        <div class="d-flex align-items-center">
                          <span class="room-icon-pill mr-2">
                            <CIcon name="cil-door" size="sm" />
                          </span>
                          <strong class="text-dark room-name-text">{{ room.ROOMNAME }}</strong>
                        </div>
                      </td>
                      <td class="text-center">
                        <div class="d-flex justify-content-center align-items-center gap-1 action-buttons">
                          <CButton
                            size="sm"
                            class="staff-ui-action staff-ui-action--edit mr-1"
                            @click="openEditModal(room)"
                            v-c-tooltip="$t('buildingMgmt.editTooltip')"
                          >
                            <CIcon name="cil-pencil" />
                            <span>{{ $t('mgmt.edit') }}</span>
                          </CButton>
                          <CButton
                            size="sm"
                            class="staff-ui-action staff-ui-action--delete"
                            @click="confirmDeleteRoom(room, b)"
                            v-c-tooltip="$t('mgmt.delete')"
                          >
                            <CIcon name="cil-trash" />
                            <span>{{ $t('mgmt.delete') }}</span>
                          </CButton>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty state when building has 0 rooms -->
              <div v-else class="text-center py-4 bg-light rounded text-muted">
                <CIcon name="cil-info" size="lg" class="mb-2 text-secondary" />
                <p class="mb-2 font-weight-bold">{{ $t('buildingMgmt.noRooms') }}</p>
                <button
                  type="button"
                  class="btn btn-sm btn-primary"
                  @click="openAddModalForBuilding(b)"
                >
                  <CIcon name="cil-plus" size="sm" class="mr-1" />
                  {{ $t('buildingMgmt.addRoomBtn') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Empty search results -->
          <div v-if="filteredBuildings.length === 0" class="text-center py-5 bg-white border shadow-sm rounded-lg">
            <CIcon name="cil-building" size="2xl" class="text-muted mb-3" />
            <h5 class="text-dark font-weight-bold">{{ $t('staffMgmt.noResults') || 'ไม่พบข้อมูลอาคาร' }}</h5>
            <p class="text-muted small mb-0">{{ $t('buildingMgmt.searchPlaceholder') }}</p>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Add Building Modal -->
    <CModal size="md" :show.sync="addModal" class="management-add-modal" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('buildingMgmt.addBuildingModal') }}</h5>
      </template>
      <div>
        <CInput :label="$t('buildingMgmt.nameTh')" v-model="newNameTh" />
        <CInput :label="$t('buildingMgmt.nameEn')" v-model="newNameEn" />
        <CInput :label="$t('buildingMgmt.roomName')" :placeholder="$t('buildingMgmt.roomPh')" v-model="newRoomName" />
      </div>
      <template #footer>
        <CButton class="management-modal-cancel admin-action-btn" @click="addModal = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton class="management-modal-save admin-action-btn" @click="addDepartment">
          {{ $t('mgmt.add') }}
        </CButton>
      </template>
    </CModal>

    <!-- Edit Room Modal -->
    <CModal size="md" :show.sync="editModal" class="management-edit-modal" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('buildingMgmt.editRoomModal') }}</h5>
      </template>
      <div>
        <CInput :label="$t('buildingMgmt.newRoomName')" v-model="editingRoomName" />
      </div>
      <template #footer>
        <CButton class="management-modal-cancel admin-action-btn" @click="editModal = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton class="management-modal-save admin-action-btn" @click="submitEdit">
          {{ $t('buildingMgmt.saveEditBuilding') }}
        </CButton>
      </template>
    </CModal>

    <!-- Edit Building Modal -->
    <CModal size="md" :show.sync="editBuildingModal" class="management-edit-modal" centered :close-button="false">
      <template #header>
        <h5 class="modal-title mb-0 font-weight-bold">{{ $t('buildingMgmt.editDataModal') }}</h5>
      </template>
      <div class="mb-3">
        <CInput :label="$t('buildingMgmt.buildingName')" v-model="editingNewBuildingName" disabled />
        <CInput :label="$t('buildingMgmt.nameTh')" v-model="editingNameTh" />
        <CInput :label="$t('buildingMgmt.nameEn')" v-model="editingNameEn" />
      </div>
      <div>
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label class="mb-0">{{ $t('buildingMgmt.roomNamesLabel') }}</label>
          <CButton v-if="selectedRoomsToDelete.length > 0" color="danger" size="sm" class="admin-action-btn" @click="deleteSelectedRoomsInModal()">
            <CIcon name="cil-trash" class="mr-1" /> {{ $t('staffMgmt.deleteSelected') }} ({{ selectedRoomsToDelete.length }})
          </CButton>
        </div>
        <div v-for="(room, index) in editingRooms" :key="index" v-show="!room.isDeleted" class="mb-2 d-flex align-items-center">
          <input type="checkbox" class="mr-2" :value="index" v-model="selectedRoomsToDelete" style="cursor: pointer; width: 1.2em; height: 1.2em;" />
          <CInput v-model="room.ROOMNAME" :placeholder="$t('buildingMgmt.roomInputPh')" class="mb-0 flex-grow-1" style="width: 100%;">
            <template #prepend-content><CIcon name="cil-room"/></template>
          </CInput>
        </div>
        <CButton class="management-modal-add mt-2" @click="addEmptyRoom"><CIcon name="cil-plus"/> {{ $t('buildingMgmt.addRoomBtn') }}</CButton>
      </div>
      <template #footer>
        <CButton class="management-modal-cancel admin-action-btn" @click="editBuildingModal = false">
          {{ $t('mgmt.cancel') }}
        </CButton>
        <CButton class="management-modal-save admin-action-btn" @click="submitEditBuilding">
          {{ $t('buildingMgmt.saveEditBuilding') }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import CModal from '@/components/common/AccessibleModal.vue';
import { mapGetters } from "vuex";
import Swal from "sweetalert2";
import { formatAdminSwalButton } from "@/utils/sweetalertButtonHelper";
import { getBuildingEnglishName, getBuildingLocalizedName } from "@/utils/buildingBilingual";

export default {
  components: { CModal },
  data() {
    return {
      selectedBuildings: [],
      selectedRoomsToDelete: [],
      addModal: false,
      newBuildingCode: "",
      newRoomName: "",
      newNameTh: "",
      newNameEn: "",
      editingBuildingId: "",
      editingNameTh: "",
      editingNameEn: "",
      editModal: false,
      editingRoomId: "",
      editingRoomName: "",
      editBuildingModal: false,
      editingOldBuildingName: "",
      editingNewBuildingName: "",
      editingRooms: [],
      searchKeyword: "",
      expandedBuildings: {},
      allExpanded: false,
    };
  },

  computed: {
    ...mapGetters("building", ["buildings", "loading"]),

    groupedBuildingsData() {
      return this.buildings || [];
    },

    filteredBuildings() {
      const kw = (this.searchKeyword || "").toLowerCase().trim();
      const list = this.groupedBuildingsData;
      if (!kw) return list;
      return list.filter((b) => {
        const code = (b.buildingCode || b.BUILDINGCODE || '').toLowerCase();
        const th = (b.BUILDINGNAME_TH || '').toLowerCase();
        const en = (b.BUILDINGNAME_EN || '').toLowerCase();
        const label = this.buildingLabel(b).toLowerCase();
        const roomsMatch = (b.rooms || []).some((r) =>
          (r.ROOMNAME || '').toLowerCase().includes(kw)
        );
        return (
          code.includes(kw) ||
          th.includes(kw) ||
          en.includes(kw) ||
          label.includes(kw) ||
          roomsMatch
        );
      });
    },

    totalRoomsCount() {
      return (this.groupedBuildingsData || []).reduce(
        (sum, b) => sum + (b.rooms ? b.rooms.length : 0),
        0
      );
    },

    isAllSelected() {
      return (
        this.filteredBuildings.length > 0 &&
        this.selectedBuildings.length === this.filteredBuildings.length
      );
    },
  },

  methods: {
    buildingLabel(building) {
      return getBuildingLocalizedName(building, this.$store.state.locale.lang);
    },

    getBuildingEnglishName(code) {
      return getBuildingEnglishName(code);
    },

    secondaryBuildingLabel(b) {
      const isEn = this.$store.state.locale.lang === 'en';
      if (isEn) {
        return b.BUILDINGNAME_TH || '';
      } else {
        return b.BUILDINGNAME_EN || getBuildingEnglishName(b) || '';
      }
    },

    isBuildingExpanded(buildingId) {
      if (this.searchKeyword) return true;
      return Boolean(this.expandedBuildings[buildingId]);
    },

    toggleBuilding(buildingId) {
      this.$set(this.expandedBuildings, buildingId, !this.isBuildingExpanded(buildingId));
    },

    toggleAllBuildings() {
      this.allExpanded = !this.allExpanded;
      const map = {};
      this.filteredBuildings.forEach((b) => {
        map[b._id] = this.allExpanded;
      });
      this.expandedBuildings = map;
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.selectedBuildings = this.filteredBuildings.map((b) => b._id);
      } else {
        this.selectedBuildings = [];
      }
    },

    openAddModal() {
      this.newBuildingCode = "";
      this.newRoomName = "";
      this.newNameTh = "";
      this.newNameEn = "";
      this.addModal = true;
    },

    openAddModalForBuilding(b) {
      this.newBuildingCode = b.buildingCode || b.BUILDINGCODE || "";
      this.newNameTh = b.BUILDINGNAME_TH || "";
      this.newNameEn = b.BUILDINGNAME_EN || "";
      this.newRoomName = "";
      this.addModal = true;
    },

    async addDepartment() {
      try {
        const buildingCode = (this.newBuildingCode || this.newNameTh || this.newNameEn || "").trim();
        await this.$store.dispatch("building/addBuilding", {
          BUILDINGCODE: buildingCode,
          ...(this.newNameTh.trim() ? { BUILDINGNAME_TH: this.newNameTh.trim() } : {}),
          ...(this.newNameEn.trim() ? { BUILDINGNAME_EN: this.newNameEn.trim() } : {}),
          ROOMNAME: this.newRoomName.trim()
        });

        await this.$store.dispatch("building/refreshBuildings");
        Swal.fire(this.$t("mgmt.success"), this.$t("buildingMgmt.addOk"), "success");
        this.addModal = false;
        this.newBuildingCode = "";
        this.newRoomName = "";
        this.newNameTh = "";
        this.newNameEn = "";
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.addFail"),
          "error"
        );
      }
    },

    openEditBuildingModal(building) {
      this.editingBuildingId = building._id;
      this.editingNameTh = building.BUILDINGNAME_TH || building.buildingCode;
      this.editingNameEn = building.BUILDINGNAME_EN || getBuildingEnglishName(building);
      this.editingOldBuildingName = building.buildingCode;
      this.editingNewBuildingName = building.buildingCode;
      this.editingRooms = JSON.parse(JSON.stringify(building.rooms || [])).map((r) => ({
        ...r,
        _originalRoomName: (r.ROOMNAME || "").trim()
      }));
      this.selectedRoomsToDelete = [];
      if (this.editingRooms.length === 0) {
        this.editingRooms.push({ ROOMNAME: '' });
      }
      this.editBuildingModal = true;
    },

    deleteSelectedRoomsInModal() {
      for (const i of this.selectedRoomsToDelete) {
        this.$set(this.editingRooms[i], 'isDeleted', true);
        this.editingRooms[i].ROOMNAME = "";
      }
      this.selectedRoomsToDelete = [];
    },

    addEmptyRoom() {
      this.editingRooms.push({ ROOMNAME: '' });
    },

    async submitEditBuilding() {
      try {
        const promises = [];

        await this.$store.dispatch("building/editBuildingNames", {
          buildingId: this.editingBuildingId,
          BUILDINGNAME_TH: this.editingNameTh,
          BUILDINGNAME_EN: this.editingNameEn
        });

        for (const room of this.editingRooms) {
          const roomName = room.ROOMNAME ? room.ROOMNAME.trim() : "";
          if (room._id || room.ROOMID) {
            const roomId = room._id || room.ROOMID;
            if (roomName === "") {
              promises.push(this.$store.dispatch("building/deleteRoom", roomId));
            } else {
              if (roomName !== (room._originalRoomName || "")) {
                promises.push(
                  this.$store.dispatch("building/editRoom", {
                    roomId: roomId,
                    newRoomName: roomName
                  })
                );
              }
            }
          } else {
            if (roomName !== "") {
              promises.push(
                this.$store.dispatch("building/addBuilding", {
                  BUILDINGCODE: this.editingNewBuildingName,
                  ROOMNAME: roomName
                })
              );
            }
          }
        }

        await Promise.all(promises);
        await this.$store.dispatch("building/refreshBuildings");

        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.saveSaved"), "success");
        this.editBuildingModal = false;
      } catch (err) {
        console.error(err);
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.saveFail"),
          "error"
        );
      }
    },

    openEditModal(room) {
      this.editingRoomId = room._id || room.ROOMID;
      this.editingRoomName = room.ROOMNAME;
      this.editModal = true;
    },

    async submitEdit() {
      try {
        await this.$store.dispatch("building/editRoom", {
          roomId: this.editingRoomId,
          newRoomName: this.editingRoomName
        });
        await this.$store.dispatch("building/refreshBuildings");
        Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.editRoomSaved"), "success");
        this.editModal = false;
      } catch (err) {
        Swal.fire(
          this.$t("mgmt.error"),
          err.response?.data?.message || this.$t("mgmt.editFail"),
          "error"
        );
      }
    },

    async confirmDelete(item) {
      const result = await Swal.fire({
        title: this.$t("mgmt.deleteBuildingTitle"),
        text: this.$t("buildingMgmt.deleteBuildingLine").replace("{name}", item.buildingCode || item.BUILDINGCODE),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#ced4da',
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.delete")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel"))
      });

      if (result.isConfirmed) {
        const result2 = await Swal.fire({
          title: this.$t("mgmt.deleteBuildingSure"),
          text: this.$t("buildingMgmt.buildingWillDeleteLine").replace("{name}", item.buildingCode || item.BUILDINGCODE),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#e55353',
          cancelButtonColor: '#ced4da',
          confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete")),
          cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel"))
        });

        if (result2.isConfirmed) {
          try {
            await this.$store.dispatch('building/deleteBuilding', item._id);
            await this.$store.dispatch("building/refreshBuildings");
            Swal.fire(this.$t("mgmt.deleteOk"), this.$t("mgmt.deleteBuildingDone"), 'success');
          } catch (err) {
            Swal.fire(this.$t("mgmt.error"), this.$t("mgmt.cannotDelete"), 'error');
          }
        }
      }
    },

    async confirmDeleteRoom(room, building) {
      const roomName = room.ROOMNAME || "";
      const result = await Swal.fire({
        title: this.$t("buildingMgmt.deleteRoomTitle") || "ยืนยันการลบห้อง",
        text: this.$t("buildingMgmt.deleteRoomConfirm")
          ? this.$t("buildingMgmt.deleteRoomConfirm").replace("{name}", roomName)
          : `คุณต้องการลบห้อง "${roomName}" ใช่หรือไม่?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#ced4da',
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.delete")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel"))
      });

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('building/deleteRoom', room._id || room.ROOMID);
          await this.$store.dispatch("building/refreshBuildings");
          Swal.fire(this.$t("mgmt.success"), this.$t("mgmt.deleteOk"), "success");
        } catch (err) {
          Swal.fire(
            this.$t("mgmt.error"),
            err.response?.data?.message || this.$t("mgmt.deleteFail"),
            "error"
          );
        }
      }
    },

    async deleteMultipleBuildings() {
      const result = await Swal.fire({
        title: this.$t("mgmt.deleteBuildingBulkTitle"),
        text: this.$t("buildingMgmt.deleteBulkLine").replace("{n}", String(this.selectedBuildings.length)),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#e55353',
        cancelButtonColor: '#ced4da',
        confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.delete")),
        cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel"))
      });

      if (result.isConfirmed) {
        const result2 = await Swal.fire({
          title: this.$t("mgmt.deleteBuildingSure"),
          text: this.$t("mgmt.buildingsSelectedWillDelete"),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#e55353',
          cancelButtonColor: '#ced4da',
          confirmButtonText: formatAdminSwalButton('trash', this.$t("mgmt.confirmDelete")),
          cancelButtonText: formatAdminSwalButton('ban', this.$t("mgmt.cancel"))
        });

        if (result2.isConfirmed) {
          Swal.fire({
            title: this.$t("buildingMgmt.deletingTitle"),
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });

          let successCount = 0;
          let failCount = 0;

          for (const bId of this.selectedBuildings) {
            try {
              await this.$store.dispatch('building/deleteBuilding', bId);
              successCount++;
            } catch (err) {
              failCount++;
            }
          }

          Swal.fire({
            title: failCount === 0 ? this.$t("buildingMgmt.batchResultTitle") : this.$t("mgmt.doneTitle"),
            html: this.$t("buildingMgmt.batchResultHtml").replace("{ok}", String(successCount)) + (failCount > 0 ? `<br>${this.$t("buildingMgmt.batchFailLine").replace("{fail}", String(failCount))}` : ""),
            icon: failCount === 0 ? "success" : "warning"
          });

          this.selectedBuildings = [];
        }
      }
    },
  },

  mounted() {
    this.$store.dispatch("building/fetchBuildings");
    this.$store.dispatch("building/initSocket");
  }
};
</script>

<style scoped>
.building-management-page {
  animation: fadeIn 0.2s ease-in-out;
}

.header-main-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #ffffff;
  flex-shrink: 0;
}

.header-sub-text {
  font-size: 0.85rem;
  color: #e2e8f0;
}

/* Control Bar Surface */
.control-bar-surface {
  border-radius: 12px;
  background-color: #ffffff;
  border: 1px solid #e2e8f0 !important;
}

.search-input-group {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border-radius: 8px;
  overflow: hidden;
}

.search-input-group .form-control {
  border-color: #e2e8f0;
  font-size: 0.9rem;
  padding: 9px 12px;
  height: auto;
}

.search-input-group .form-control:focus {
  border-color: #2563eb;
  box-shadow: none;
}

.btn-admin-action {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  font-size: 0.825rem;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  min-height: 38px;
}

.btn-admin-action:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.badge-stat {
  font-size: 0.825rem;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  min-height: 38px;
}

.badge-stat--building {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #dbeafe;
}

.badge-stat--rooms {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

/* Buildings Container */
.buildings-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Building Big Box */
.building-big-box {
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03) !important;
  transition: all 0.2s ease;
  overflow: hidden;
  margin-bottom: 0 !important;
}

.building-big-box.building-box--has-rooms {
  border-color: #bfdbfe !important;
}

.building-big-box:hover {
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.06) !important;
}

.building-header {
  background: #1e40af !important;
  user-select: none;
  padding: 16px 20px !important;
  transition: background 0.2s ease;
}

.building-header:hover {
  background: #1d4ed8 !important;
}

.building-select-checkbox {
  cursor: pointer;
  width: 1.25em;
  height: 1.25em;
  accent-color: #1e40af;
}

.building-badge-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #ffffff;
  color: #1e40af;
  flex-shrink: 0;
  margin-right: 16px;
}

.building-title-text {
  font-size: 1.125rem;
  letter-spacing: 0.1px;
  color: #ffffff !important;
}

.badge-code--building {
  background: rgba(255, 255, 255, 0.2);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.3);
  font-size: 0.72rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.badge-count-pill {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}

.badge-count-pill--has-rooms {
  background: #dcfce7;
  color: #15803d;
}

.badge-count-pill--empty {
  background: rgba(255, 255, 255, 0.16);
  color: #f1f5f9;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.building-meta-text {
  color: #e2e8f0 !important;
  font-size: 0.84rem;
  font-weight: 500;
}

/* Action Buttons */
.btn-building-action {
  border-radius: 8px;
  font-size: 0.825rem;
  font-weight: 600;
  padding: 6px 14px;
  min-height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.btn-building-action--add-room {
  background: #ffffff;
  color: #1e40af;
  border: 1px solid #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.btn-building-action--add-room:hover {
  background: #f0f9ff;
  transform: translateY(-1px);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.btn-building-action--edit {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.btn-building-action--edit:hover {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.4);
}

.btn-building-action--delete {
  background: rgba(220, 38, 38, 0.2);
  color: #fee2e2;
  border: 1px solid rgba(248, 113, 113, 0.35);
}

.btn-building-action--delete:hover {
  background: #dc2626;
  color: #ffffff;
  border-color: #dc2626;
}

.btn-building-action--toggle {
  background: rgba(255, 255, 255, 0.12);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.28);
}

.btn-building-action--toggle:hover {
  background: rgba(255, 255, 255, 0.22);
  border-color: rgba(255, 255, 255, 0.4);
}

/* Room Nested Table */
.room-nested-table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.room-table-thead {
  background: #f8fafc;
}

.room-table-thead th {
  font-size: 0.825rem;
  font-weight: 700;
  color: #475569;
  border-top: none;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}

.room-nested-table td {
  padding: 12px 16px;
  vertical-align: middle;
  border-bottom: 1px solid #f1f5f9;
}

.room-icon-pill {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #eff6ff;
  color: #2563eb;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.room-name-text {
  font-size: 0.95rem;
  color: #1e293b;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .building-header {
    flex-direction: column;
    align-items: flex-start;
  }
  .building-actions-wrapper {
    width: 100%;
    margin-top: 12px;
    justify-content: flex-start;
  }
  .control-bar-surface {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
